import React, { useMemo } from "react";
import styled from "styled-components";
import type { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { PlanogramBay } from "@CommonTypes/merchflow/pog/bay";
import { Flex } from "src/elements";
import { useTranslation } from "src/i18n";
import { DEFAULT_NOTCH_RULER_WIDTH, getBayPdfShift } from "../Ruler/PdfNotchRulers";
import { getSplitShifts } from "../KonvaPlanogram/sceneProjection";
import { getShelfRenderGroups } from "./Shelves";
import { getDecorativeShelfImage } from "./ShelfBottom";
import { ShelfLabel } from "./ShelfLabel";
import type { PlanogramWorldOverlayState } from "../KonvaPlanogram/types";

const DecorativeShelvesOverlayLayer = styled(Flex)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;
`;

const DecorativeShelfFrame = styled.div<{
  xLeft: number;
  xRight: number;
  yBottom: number;
  thickness: number;
  scaleX: number;
  scaleY: number;
}>`
  position: absolute;
  left: ${({ xLeft, scaleX }) => `calc(${xLeft}px * ${scaleX})`};
  bottom: ${({ yBottom, scaleY }) => `calc(${yBottom}px * ${scaleY})`};
  width: ${({ xLeft, xRight, scaleX }) => `calc(${xRight - xLeft}px * ${scaleX})`};
  height: ${({ thickness, scaleY }) => `calc(${thickness}px * ${scaleY})`};
  pointer-events: none;
`;

const DecorativeShelfImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: fill;
  pointer-events: none;
`;

const DecorativeShelfLabel = styled(ShelfLabel)`
  z-index: 1;
`;

const DECORATIVE_SHELF_MERCHANT = "711-malaysia";
const DECORATIVE_SHELF_CATEGORY = "chocolate";
export const DECORATIVE_SHELF_THICKNESS = 400;

const isDecorativeShelfScope = (
  merchantName: string | null | undefined,
  categoryName: string | null | undefined,
) =>
  merchantName?.trim().toLowerCase() === DECORATIVE_SHELF_MERCHANT &&
  categoryName?.trim().toLowerCase() === DECORATIVE_SHELF_CATEGORY;

interface Props {
  bays: PlanogramBay[];
  scaleX: number;
  scaleY: number;
  merchantName?: string | null;
  categoryName?: string | null;
  pdfMode?: boolean;
  pdfShelfLabelBayNo?: number;
  pdfNotchRulerWidth?: number;
  shelfDragPreview?: PlanogramWorldOverlayState["shelfDragPreview"];
}

export const DecorativeShelves = ({
  bays,
  scaleX,
  scaleY,
  merchantName,
  categoryName,
  pdfMode = false,
  pdfShelfLabelBayNo,
  pdfNotchRulerWidth,
  shelfDragPreview,
}: Props) => {
  const { t } = useTranslation("common");
  const isInScope = isDecorativeShelfScope(merchantName, categoryName);
  const shelfDragIds = useMemo(
    () => new Set(shelfDragPreview?.shelfIds ?? []),
    [shelfDragPreview?.shelfIds],
  );
  const decorativeShelvesToRender = useMemo(() => {
    if (!isInScope) return [];

    const list: Array<{
      key: string;
      imageSrc: string;
      shelf: PlanogramShelf;
      shelfLabelBayNo?: number;
      xLeft: number;
      xRight: number;
      yBottom: number;
      thickness: number;
      shelfIds: readonly string[];
    }> = [];

    const splitShifts = getSplitShifts(bays);
    bays.forEach((bay, bayIndex) => {
      const rulerWidth = pdfNotchRulerWidth ?? DEFAULT_NOTCH_RULER_WIDTH;
      const bayShift = (pdfMode ? getBayPdfShift(bayIndex, rulerWidth) : 0) + splitShifts[bayIndex];
      const renderGroups = getShelfRenderGroups(bay.shelves);
      for (const { shelf, shelvesToRender } of renderGroups) {
        const imageSrc = getDecorativeShelfImage(shelf.uniqueId);
        if (
          imageSrc &&
          shelf.thickness >= DECORATIVE_SHELF_THICKNESS &&
          shelvesToRender.length > 0
        ) {
          const firstShelf = shelvesToRender[0];
          const lastShelf = shelvesToRender[shelvesToRender.length - 1];
          list.push({
            key: `${bay.uniqueId}-${shelf.uniqueId}`,
            imageSrc,
            shelf: firstShelf,
            shelfLabelBayNo: pdfShelfLabelBayNo ?? (pdfMode ? bay.getNo() : undefined),
            xLeft: firstShelf.getXLeft() + bayShift,
            xRight: lastShelf.getXRight() + bayShift,
            yBottom: firstShelf.getYBottom(),
            thickness: firstShelf.thickness,
            shelfIds: shelvesToRender.map(({ uniqueId }) => uniqueId),
          });
        }
      }
    });

    return list;
  }, [bays, isInScope, pdfMode, pdfNotchRulerWidth, pdfShelfLabelBayNo]);

  if (decorativeShelvesToRender.length === 0) return null;

  return (
    <DecorativeShelvesOverlayLayer className="decorative-shelves-overlay">
      {decorativeShelvesToRender.map((item) => (
        <DecorativeShelfFrame
          key={item.key}
          xLeft={item.xLeft}
          xRight={item.xRight}
          yBottom={
            item.yBottom +
            (shelfDragPreview && item.shelfIds.some((shelfId) => shelfDragIds.has(shelfId))
              ? -shelfDragPreview.deltaY
              : 0)
          }
          thickness={item.thickness}
          scaleX={scaleX}
          scaleY={scaleY}
          data-testid="decorative-shelf-frame"
        >
          <DecorativeShelfImage src={item.imageSrc} alt="Decorative Shelf" />
          {pdfMode && (
            <DecorativeShelfLabel
              shelf={item.shelf}
              shelfLabelBayNo={item.shelfLabelBayNo}
              t={t}
              testId="decorative-shelf-label"
            />
          )}
        </DecorativeShelfFrame>
      ))}
    </DecorativeShelvesOverlayLayer>
  );
};
